// useRoleGate.js — Lightweight role-gating helpers for the UI.
//
// Two ways to use:
//
//   1. Hook (when you need conditional logic, not just visibility)
//      ────────────────────────────────────────────────────────────
//      const canSeed = useRoleGate("steward");
//      if (!canSeed) return null;
//
//   2. Component (when you just want to wrap JSX)
//      ────────────────────────────────────────────────────────────
//      <RoleGate role="steward">
//        <SampleDataCard />
//      </RoleGate>
//
// Both accept a named set (`"steward"`, `"platform_admin"`) or a
// custom array (`["admin", "manager"]`). Resolution lives in
// `lib/roles.js` so behaviour stays canonical.

import React from "react";
import { useAuth } from "../data/authStore";
import { roleAllowed } from "../lib/roles";

/** Hook — returns `true` when the current user satisfies `gate`. */
export const useRoleGate = (gate) => {
  const { user } = useAuth();
  return roleAllowed(user?.role, gate);
};

/** Component — renders children only when current user passes `role`. */
export const RoleGate = ({ role, fallback = null, children }) => {
  const allowed = useRoleGate(role);
  return allowed ? <>{children}</> : fallback;
};

export default useRoleGate;
